import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef, useCallback } from 'react';
import { Ticket, AttendeeInfo } from '../../../types';
import TicketCard from '../TicketCard/TicketCard';
import Card from '../../ui/Card/Card';
import { isValidEmail, isLettersAndSpaces, isDigitsOnly, isValidPhoneNumberWithCountry, isValidPhoneNumberForCountry } from '../../../utils/validators';
import { countryPhoneCodes, CountryPhoneCode, getMaxLengthForCountry } from '../../../utils/CountryPhoneCodes';
import CountryCodeSelector from '../../ui/CountryCodeSelector/CountryCodeSelector';
import { useAutofillSync } from '../../../hooks/useAutofillSync';
import styles from './TicketSelection.module.css';

const LCV_TICKET_TYPE = 'togather-join-ticket';

const isLcvJoinTicketType = (type?: string) =>
  (type || '').trim().toLowerCase() === LCV_TICKET_TYPE;

type AttendeeField = 'name' | 'lastname' | 'email' | 'phone';

interface TicketSelectionProps {
  tickets: Ticket[];
  channelID: string;
  eventID: string;
  userInfo?: {
    name?: string;
    lastname?: string;
    email?: string;
    phone?: string;
  };
  onCheckout: (attendees: AttendeeInfo[], totalPrice: number) => void;
  onValidityChange?: (isValid: boolean) => void;
  getTranslation: (key: string) => string;
}

export interface TicketSelectionRef {
  validate: () => { ok: boolean; msg?: string };
  isFormValid: () => boolean;
}

const TicketSelection = forwardRef<TicketSelectionRef, TicketSelectionProps>(({
  tickets,
  channelID,
  eventID,
  userInfo,
  onCheckout,
  onValidityChange,
  getTranslation
}, ref) => {
  const [ticketQuantities, setTicketQuantities] = useState<Record<string, number>>({});
  const [attendees, setAttendees] = useState<Record<string, AttendeeInfo[]>>({});
  const [errors, setErrors] = useState<Record<string, Record<number, Record<string, string>>>>({});
  const [countryCodes, setCountryCodes] = useState<Record<string, Record<number, string>>>({});
  const attendeesCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Initialize quantities
  useEffect(() => {
    const initialQuantities: Record<string, number> = {};
    if (tickets && Array.isArray(tickets)) {
      tickets.forEach(ticket => {
        initialQuantities[ticket.ticketID] = 0;
      });
    }
    setTicketQuantities(initialQuantities);
  }, [tickets]);

  const handleIncrement = (ticketID: string) => {
    // Check if this is the first ticket being added (when there are currently 0 tickets)
    const totalTicketsBefore = Object.values(ticketQuantities).reduce((sum, qty) => sum + qty, 0);
    const isFirstTicket = totalTicketsBefore === 0;
    // Autofill whenever adding the first ticket (when there are 0 tickets)
    const shouldAutoFill = isFirstTicket && userInfo;

    // Find the ticket
    const ticket = tickets.find(t => t.ticketID === ticketID);
    if (!ticket) return;

    // Update quantities
    setTicketQuantities(prev => ({
      ...prev,
      [ticketID]: (prev[ticketID] || 0) + 1
    }));

    // Add new attendee info - only one attendee per increment
    setAttendees(prev => {
      const existing = prev[ticketID] || [];
      const newAttendee: AttendeeInfo = {
        name: shouldAutoFill ? (userInfo?.name || '') : '',
        lastname: shouldAutoFill ? (userInfo?.lastname || '') : '',
        email: shouldAutoFill ? (userInfo?.email || '') : '',
        phone: shouldAutoFill ? (userInfo?.phone || '') : '',
        ticketType: ticket.type,
        ticketTitle: ticket.title,
        ticketPrice: typeof ticket.price === 'number' ? ticket.price : Number(ticket.price) || 0,
        ticketID: ticket.ticketID,
        channelID,
        eventID
      };
      
      const updated = {
        ...prev,
        [ticketID]: [...existing, newAttendee]
      };
      
      // Initialize country code for new attendee (default to +90)
      setCountryCodes(prev => {
        const existingCodes = prev[ticketID] || {};
        return {
          ...prev,
          [ticketID]: {
            ...existingCodes,
            [existing.length]: '+90' // Default to Turkey
          }
        };
      });
      
      // Notify parent of attendee changes
      const allAttendees = Object.values(updated).flat();
      const totalPrice = allAttendees.reduce((sum, attendee) => {
        const price = typeof attendee.ticketPrice === 'number' ? attendee.ticketPrice : Number(attendee.ticketPrice) || 0;
        return sum + price;
      }, 0);
      onCheckout(allAttendees, totalPrice);
      
      // Scroll to and focus attendee form after adding
      setTimeout(() => {
        const attendeesCard = attendeesCardRefs.current[ticketID];
        if (attendeesCard) {
          attendeesCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          // Focus first input in the new attendee form
          const firstInput = attendeesCard.querySelector<HTMLInputElement>('input[type="text"]');
          if (firstInput) {
            setTimeout(() => {
              firstInput.focus();
            }, 300);
          }
        }
      }, 100);
      
      return updated;
    });

  };

  const handleDecrement = (ticketID: string) => {
    // Update quantities
    setTicketQuantities(prev => ({
      ...prev,
      [ticketID]: Math.max(0, (prev[ticketID] || 0) - 1)
    }));

    // Remove last attendee
    setAttendees(prev => {
      const existing = prev[ticketID] || [];
      const lastIndex = existing.length - 1;
      const updated = {
        ...prev,
        [ticketID]: existing.slice(0, -1)
      };
      
      // Remove country code for removed attendee
      setCountryCodes(prev => {
        const existingCodes = prev[ticketID] || {};
        const updatedCodes = { ...existingCodes };
        delete updatedCodes[lastIndex];
        // Reindex remaining codes
        const reindexed: Record<number, string> = {};
        Object.keys(updatedCodes).forEach((key, newIndex) => {
          reindexed[newIndex] = updatedCodes[Number(key)];
        });
        return {
          ...prev,
          [ticketID]: reindexed
        };
      });
      
      // Notify parent of attendee changes
      const allAttendees = Object.values(updated).flat();
      const totalPrice = allAttendees.reduce((sum, attendee) => {
        const price = typeof attendee.ticketPrice === 'number' ? attendee.ticketPrice : Number(attendee.ticketPrice) || 0;
        return sum + price;
      }, 0);
      onCheckout(allAttendees, totalPrice);
      
      return updated;
    });
  };

  const handleAttendeeChange = (
    ticketID: string,
    index: number,
    field: keyof AttendeeInfo,
    value: string
  ) => {
    // Apply input filtering based on field type
    let filteredValue = value;
    if (field === 'name' || field === 'lastname') {
      // Only allow letters and spaces (including Turkish characters)
      filteredValue = value.replace(/[^A-Za-zÇĞİÖŞÜçğıöşü\s]/g, '');
    } else if (field === 'phone') {
      // Only allow digits and restrict to max length based on selected country
      let digitsOnly = value.replace(/\D/g, '');
      const countryCode = countryCodes[ticketID]?.[index] || '+90';
      const country = countryPhoneCodes.find(c => c.code === countryCode);
      const maxLength = getMaxLengthForCountry(country);

      // Handle browser autofill: browsers on deployed environments (HTTPS, real domain)
      // often autofill with the full international number (e.g. "+905551234567").
      // After stripping non-digits we get "905551234567" which exceeds maxLength.
      // Detect this case and strip the leading country code so we keep only
      // the national part (e.g. "5551234567").
      const codeDigits = countryCode.replace(/\D/g, '');
      if (codeDigits && digitsOnly.startsWith(codeDigits) && digitsOnly.length > maxLength) {
        digitsOnly = digitsOnly.slice(codeDigits.length);
      }

      // Only allow up to maxLength digits
      filteredValue = digitsOnly.slice(0, maxLength);
    } else if (field === 'email') {
      // Email is handled by input type="email", but we can lowercase it
      filteredValue = value.toLowerCase();
    }

    setAttendees(prev => {
      const existing = prev[ticketID] || [];
      const updated = [...existing];
      updated[index] = {
        ...updated[index],
        [field]: filteredValue
      };
      const newAttendees = {
        ...prev,
        [ticketID]: updated
      };
      
      // Clear error for this field when user types
      setErrors(prev => {
        const ticketErrors = prev[ticketID] || {};
        const attendeeErrors = ticketErrors[index] || {};
        const newAttendeeErrors = { ...attendeeErrors };
        delete newAttendeeErrors[field];
        return {
          ...prev,
          [ticketID]: {
            ...ticketErrors,
            [index]: newAttendeeErrors
          }
        };
      });
      
      // Notify parent of attendee changes
      const allAttendees = Object.values(newAttendees).flat();
      const totalPrice = allAttendees.reduce((sum, attendee) => sum + attendee.ticketPrice, 0);
      onCheckout(allAttendees, totalPrice);
      
      return newAttendees;
    });
  };

  const syncAutofilledAttendees = () => {
    Object.entries(inputRefs.current).forEach(([key, input]) => {
      if (!input) return;

      const [ticketID, indexValue, field] = key.split('|') as [string, string, AttendeeField];
      const attendeeIndex = Number(indexValue);
      const attendee = attendees[ticketID]?.[attendeeIndex];

      if (!attendee) return;

      const currentValue = attendee[field] || '';
      if (input.value === currentValue) return;

      handleAttendeeChange(ticketID, attendeeIndex, field, input.value);
    });
  };

  const attendeeCount = Object.values(attendees).reduce((total, ticketAttendees) => {
    return total + ticketAttendees.length;
  }, 0);

  const scheduleAutofillSync = useAutofillSync(syncAutofilledAttendees, attendeeCount);

  // Prevent automatic scrolling when inputs are focused
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Use requestAnimationFrame to ensure proper timing
    requestAnimationFrame(() => {
      const input = e.target;
      if (input) {
        // Check if element is already in viewport
        const rect = input.getBoundingClientRect();
        const isInView = (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
        
        // Only scroll if element is not fully in view, and use 'nearest' to minimize scroll
        if (!isInView) {
          input.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
      }
    });
  };

  const validateAttendees = (): { ok: boolean; msg?: string } => {
    const newErrors: Record<string, Record<number, Record<string, string>>> = {};
    
    // Validate each attendee
    Object.entries(attendees).forEach(([ticketID, ticketAttendees]) => {
      ticketAttendees.forEach((attendee, index) => {
        const attendeeErrors: Record<string, string> = {};
        
        // Validate name
        if (!attendee.name || !attendee.name.trim()) {
          attendeeErrors.name = getTranslation('events.nameRequired') || 'Name is required';
        } else if (!isLettersAndSpaces(attendee.name)) {
          attendeeErrors.name = getTranslation('events.nameError') || 'Name can only contain letters and spaces';
        }
        
        // Validate lastname
        if (!attendee.lastname || !attendee.lastname.trim()) {
          attendeeErrors.lastname = getTranslation('events.lastnameRequired') || 'Lastname is required';
        } else if (!isLettersAndSpaces(attendee.lastname)) {
          attendeeErrors.lastname = getTranslation('events.nameError') || 'Lastname can only contain letters and spaces';
        }
        
        // Validate email
        if (!attendee.email || !attendee.email.trim()) {
          attendeeErrors.email = getTranslation('events.emailRequired') || 'Email is required';
        } else if (!isValidEmail(attendee.email)) {
          attendeeErrors.email = getTranslation('events.emailError') || 'Invalid email format';
        }
        
        // Validate phone
        if (!attendee.phone || !attendee.phone.trim()) {
          attendeeErrors.phone = getTranslation('events.phoneRequired') || 'Phone is required';
        } else {
          // Use country-based validation with selected country code
          const countryCode = countryCodes[ticketID]?.[index] || '+90'; // Default to Turkey
          const country = countryPhoneCodes.find(c => c.code === countryCode);
          
          if (country) {
            const validation = isValidPhoneNumberForCountry(attendee.phone, country);
            if (!validation.isValid) {
              attendeeErrors.phone = validation.error || getTranslation('events.phoneError') || 'Invalid phone number';
            }
          } else {
            const validation = isValidPhoneNumberWithCountry(attendee.phone, countryCode);
            if (!validation.isValid) {
              attendeeErrors.phone = validation.error || getTranslation('events.phoneError') || 'Invalid phone number';
            }
          }
        }
        
        if (Object.keys(attendeeErrors).length > 0) {
          if (!newErrors[ticketID]) {
            newErrors[ticketID] = {};
          }
          newErrors[ticketID][index] = attendeeErrors;
        }
      });
    });

    // Set errors and return result
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      return { ok: false, msg: getTranslation('events.pleaseFillAllFields') || 'Please fill all required fields correctly' };
    }
    
    return { ok: true };
  };

  // Check if all attendee fields are valid (for button disabled state)
  const isFormValid = useCallback((): boolean => {
    const allAttendees = Object.values(attendees).flat();
    
    // Check if we have any attendees
    if (allAttendees.length === 0) {
      return false;
    }
    
    // Check all fields are filled and valid
    for (const [ticketID, ticketAttendees] of Object.entries(attendees)) {
      for (let index = 0; index < ticketAttendees.length; index += 1) {
        const attendee = ticketAttendees[index];
        const countryCode = countryCodes[ticketID]?.[index] || '+90';
        const country = countryPhoneCodes.find(c => c.code === countryCode);

        if (!attendee.name?.trim() || !isLettersAndSpaces(attendee.name)) {
          return false;
        }
        if (!attendee.lastname?.trim() || !isLettersAndSpaces(attendee.lastname)) {
          return false;
        }
        if (!attendee.email?.trim() || !isValidEmail(attendee.email)) {
          return false;
        }
        if (!attendee.phone?.trim() || !isDigitsOnly(attendee.phone)) {
          return false;
        }

        if (country) {
          const validation = isValidPhoneNumberForCountry(attendee.phone, country);
          if (!validation.isValid) {
            return false;
          }
        } else {
          const validation = isValidPhoneNumberWithCountry(attendee.phone, countryCode);
          if (!validation.isValid) {
            return false;
          }
        }
      }
    }
    return true;
  }, [attendees, countryCodes]);

  useEffect(() => {
    onValidityChange?.(isFormValid());
  }, [attendees, countryCodes, isFormValid, onValidityChange]);

  // Expose validation function via ref
  useImperativeHandle(ref, () => ({
    validate: () => {
      const validation = validateAttendees();
      if (!validation.ok && validation.msg) {
        // Scroll to first error
        setTimeout(() => {
          const firstErrorField = document.querySelector(`.${styles.formFieldError} input, .${styles.inputError}`);
          if (firstErrorField) {
            firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
      return validation;
    },
    isFormValid
  }));


  // Filter out sponsor tickets (shown separately in sponsorship section)
  const nonSponsorTickets = (tickets && Array.isArray(tickets))
    ? tickets.filter((ticket) => {
      const ticketType = (ticket.type || '').trim().toLowerCase();
      return ticketType !== 'sponsor' && !isLcvJoinTicketType(ticketType);
    })
    : [];
  const sortedTickets = [...nonSponsorTickets].sort((a, b) => a.price - b.price);

  if (sortedTickets.length === 0) {
    return (
      <div className={styles.noTickets}>
        <p>{getTranslation('events.noTicketsAvailable')}</p>
      </div>
    );
  }

  return (
    <div
      className={styles.ticketSelection}
      onFocusCapture={scheduleAutofillSync}
      onBlurCapture={scheduleAutofillSync}
    >
      <div className={styles.ticketsList}>
        {sortedTickets.map(ticket => {
          const quantity = ticketQuantities[ticket.ticketID] || 0;
          const ticketAttendees = attendees[ticket.ticketID] || [];

          return (
            <div key={ticket.ticketID} className={styles.ticketSection}>
              <TicketCard
                ticket={ticket}
                quantity={quantity}
                onIncrement={() => handleIncrement(ticket.ticketID)}
                onDecrement={() => handleDecrement(ticket.ticketID)}
                getTranslation={getTranslation}
              />

              {quantity > 0 && (
                <Card 
                  className={styles.attendeesCard}
                  ref={(el) => {
                    attendeesCardRefs.current[ticket.ticketID] = el;
                  }}
                >
                  <h4 className={styles.attendeesTitle}>
                    {getTranslation('events.attendeeInformation')} ({quantity})
                  </h4>
                  {ticketAttendees.map((attendee, index) => (
                    <div key={index} className={styles.attendeeForm}>
                      <h5 className={styles.attendeeNumber}>
                        {getTranslation('events.attendee')} {index + 1}
                      </h5>
                      <div className={styles.formGrid}>
                        <div className={`${styles.formField} ${errors[ticket.ticketID]?.[index]?.name ? styles.formFieldError : ''}`}>
                          <label className={errors[ticket.ticketID]?.[index]?.name ? styles.formLabelError : ''}>
                            {getTranslation('auth.firstName')} *
                          </label>
                          <input
                            type="text"
                            value={attendee.name}
                            ref={(element) => {
                              inputRefs.current[`${ticket.ticketID}|${index}|name`] = element;
                            }}
                            onChange={(e) => handleAttendeeChange(ticket.ticketID, index, 'name', e.target.value)}
                            onFocus={handleInputFocus}
                            placeholder={getTranslation('auth.firstName')}
                            name={`ticket-attendee-${ticket.ticketID}-${index}-name`}
                            autoComplete={`section-${ticket.ticketID}-${index} given-name`}
                            className={errors[ticket.ticketID]?.[index]?.name ? styles.inputError : ''}
                          />
                          {errors[ticket.ticketID]?.[index]?.name && (
                            <span className={styles.errorMessage}>{errors[ticket.ticketID][index].name}</span>
                          )}
                        </div>
                        <div className={`${styles.formField} ${errors[ticket.ticketID]?.[index]?.lastname ? styles.formFieldError : ''}`}>
                          <label className={errors[ticket.ticketID]?.[index]?.lastname ? styles.formLabelError : ''}>
                            {getTranslation('auth.lastName')} *
                          </label>
                          <input
                            type="text"
                            value={attendee.lastname}
                            ref={(element) => {
                              inputRefs.current[`${ticket.ticketID}|${index}|lastname`] = element;
                            }}
                            onChange={(e) => handleAttendeeChange(ticket.ticketID, index, 'lastname', e.target.value)}
                            onFocus={handleInputFocus}
                            placeholder={getTranslation('auth.lastName')}
                            name={`ticket-attendee-${ticket.ticketID}-${index}-lastname`}
                            autoComplete={`section-${ticket.ticketID}-${index} family-name`}
                            className={errors[ticket.ticketID]?.[index]?.lastname ? styles.inputError : ''}
                          />
                          {errors[ticket.ticketID]?.[index]?.lastname && (
                            <span className={styles.errorMessage}>{errors[ticket.ticketID][index].lastname}</span>
                          )}
                        </div>
                        <div className={`${styles.formField} ${errors[ticket.ticketID]?.[index]?.email ? styles.formFieldError : ''}`}>
                          <label className={errors[ticket.ticketID]?.[index]?.email ? styles.formLabelError : ''}>
                            {getTranslation('auth.email')} *
                          </label>
                          <input
                            type="email"
                            value={attendee.email}
                            ref={(element) => {
                              inputRefs.current[`${ticket.ticketID}|${index}|email`] = element;
                            }}
                            onChange={(e) => handleAttendeeChange(ticket.ticketID, index, 'email', e.target.value)}
                            onFocus={handleInputFocus}
                            placeholder={getTranslation('auth.email')}
                            name={`ticket-attendee-${ticket.ticketID}-${index}-email`}
                            autoComplete={`section-${ticket.ticketID}-${index} email`}
                            autoCapitalize="none"
                            className={errors[ticket.ticketID]?.[index]?.email ? styles.inputError : ''}
                          />
                          {errors[ticket.ticketID]?.[index]?.email && (
                            <span className={styles.errorMessage}>{errors[ticket.ticketID][index].email}</span>
                          )}
                        </div>
                        <div className={`${styles.formField} ${errors[ticket.ticketID]?.[index]?.phone ? styles.formFieldError : ''}`}>
                          <label className={errors[ticket.ticketID]?.[index]?.phone ? styles.formLabelError : ''}>
                            {getTranslation('events.phone')} *
                          </label>
                          <div className={styles.phoneInputWrapper}>
                            <CountryCodeSelector
                              selectedCountryCode={countryCodes[ticket.ticketID]?.[index] || '+90'}
                              onSelect={(country: CountryPhoneCode) => {
                                setCountryCodes(prev => ({
                                  ...prev,
                                  [ticket.ticketID]: {
                                    ...(prev[ticket.ticketID] || {}),
                                    [index]: country.code
                                  }
                                }));
                                // Truncate phone number if it exceeds new country's max length
                                const maxLength = getMaxLengthForCountry(country);
                                const currentPhone = attendee.phone;
                                if (currentPhone.length > maxLength) {
                                  handleAttendeeChange(ticket.ticketID, index, 'phone', currentPhone.slice(0, maxLength));
                                }
                              }}
                            />
                            <input
                              type="tel"
                              ref={(element) => {
                                inputRefs.current[`${ticket.ticketID}|${index}|phone`] = element;
                              }}
                              autoComplete={`section-${ticket.ticketID}-${index} tel-national`}
                              value={attendee.phone}
                              onChange={(e) => handleAttendeeChange(ticket.ticketID, index, 'phone', e.target.value)}
                              onFocus={handleInputFocus}
                              placeholder={getTranslation('events.phone')}
                              name={`ticket-attendee-${ticket.ticketID}-${index}-phone`}
                              maxLength={getMaxLengthForCountry(countryPhoneCodes.find(c => c.code === (countryCodes[ticket.ticketID]?.[index] || '+90')))}
                              className={`${errors[ticket.ticketID]?.[index]?.phone ? styles.inputError : ''} ${styles.phoneInput}`}
                            />
                          </div>
                          {errors[ticket.ticketID]?.[index]?.phone && (
                            <span className={styles.errorMessage}>{errors[ticket.ticketID][index].phone}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </Card>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

TicketSelection.displayName = 'TicketSelection';

export default TicketSelection;
